import { differenceInDays } from 'date-fns';

/**
 * Catalogue of in-app tours.
 *
 * A tour is an overlay walkthrough of UI that is already on screen, offered
 * behind a help button that carries a dot until it has been clicked once. Two
 * types share the same engine:
 *
 * - `onboarding` — evergreen. The dot shows to anyone who has not clicked yet,
 *   and the tour stays available through the button forever after.
 * - `announcement` — tied to one released change. Its dot expires
 *   `ANNOUNCEMENT_WINDOW_DAYS` after `releasedAt`, so a user who arrives a year
 *   later is not flagged with last year's news. To re-announce something, add
 *   a new entry with a new `id` — seen-state is keyed by id, so everyone's dot
 *   lights again. There is deliberately no version field, because bumping one
 *   out of habit after a typo fix would re-flag every user in the product.
 *
 * A step's `target` names a `data-tour` attribute in the app. Targets are
 * resolved when the tour opens and may legitimately be absent — a sidebar is
 * unmounted when closed, not merely hidden — and a step whose target is missing
 * is dropped from that run, unless the step has a `prepare` stage that will
 * produce it. Anchoring it somewhere arbitrary instead reads as a bug, so a
 * tour shows only the steps it can actually point at.
 *
 * This file is pure data: copy, target names, and the *names* of the stages a
 * tour needs (`setup`, `prepare`). What a stage does is supplied by the adapter
 * that mounts `AppTour` beside the feature — see `AppTour` for the contract.
 * Nothing from the app may be imported here, and a test enforces it: that is
 * what keeps a fifth tour from turning this file into a hub that knows every
 * feature.
 *
 * Copy here is user-facing and product-approved — the steps and the `button`
 * that opens them, whose tooltip and label should say *which* tour it opens.
 * Treat it like UI text, not like comments — and keep it short. A step is glanced at, not read: long
 * descriptions get skipped wholesale, taking the short ones after them along
 * too. `MAX_DESCRIPTION_LENGTH` is enforced by the tests.
 *
 * ## Why a dot on a button, rather than opening by itself
 *
 * A tour that opens unbidden reaches everyone and interrupts everyone; one
 * hidden behind a plain button interrupts nobody and reaches almost nobody. The
 * dot sits between: it is noticed by most people who visit the view, costs a
 * glance, and goes away the first time they act on it. Nothing here opens over
 * a user's work.
 *
 * Whether the dot earns its keep is measurable. `AppTour` reports every start,
 * advance, dismissal and completion to the `tour` event on `dataLayer`; the
 * number to watch is starts against visits to the view. If almost nobody clicks
 * a badged button, the badge is not the fix — the copy or the placement is.
 *
 * Two caveats on reading that number. Cookiebot gates GTM, so users who decline
 * cookies are invisible and the counts undercount; and seen-state lives in
 * `localStorage`, so it measures browsers rather than people.
 */

export const TOUR_TYPE = Object.freeze({
	ONBOARDING: 'onboarding',
	ANNOUNCEMENT: 'announcement',
});

/**
 * The longest a step's description may be. Not a style preference: past roughly
 * this length a reader decides not to start.
 */
export const MAX_DESCRIPTION_LENGTH = 90;

/** How long an announcement stays interesting after its release date. */
export const ANNOUNCEMENT_WINDOW_DAYS = 60;

/** Tour ids, so consumers don't pass raw strings around. */
export const TOUR_ID = Object.freeze({
	COLLECTION_VIEW: 'collection-view',
	AI_DETECTION: 'ai-detection',
	VIDEO_PLAYER: 'video-player',
	VIDEO_COUNT_PANEL: 'video-count-panel',
	VIDEO_MAP: 'video-map',
	VIDEO_NEW_PROJECT: 'video-new-project',
	VIDEO_CORRECTING: 'video-correcting',
	VIDEO_CHECK: 'video-check',
});

const COLLECTION_VIEW_TOUR = {
	id: TOUR_ID.COLLECTION_VIEW,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'Tour this view', label: 'Tour the collection view' },
	// Steps 3, 5 and 6 point into the Layers sidebar, which is unmounted when
	// closed; `ProjectTour` supplies the stage that opens it.
	setup: 'layerView',
	steps: [
		{
			target: 'collection-picker',
			title: 'Pick a collection',
			description: 'Choose a collection to view its detections on the map.',
			placement: 'bottom',
		},
		{
			target: 'ai-detection',
			title: 'Run a new detection',
			description: 'Pick a collection, choose an area, then run.',
			placement: 'bottom',
		},
		{
			target: 'layer-view',
			title: 'Everything drawn on the map',
			description: 'Turn layers on or off, reorder them, open their settings.',
			placement: 'right',
		},
		{
			target: 'map-legend',
			title: 'What the colors mean',
			description: 'Which color on the map belongs to which object.',
			placement: 'left',
		},
		{
			target: 'collection-objects',
			title: 'Filter and color by value',
			description: 'Filter detections by value, or recolor an object to spot a pattern.',
			placement: 'right',
		},
		{
			target: 'mode-switch',
			title: 'Model view is still here',
			description: 'The same project, analyzed by a trained model instead.',
			placement: 'right',
		},
	],
};

/**
 * Walks the detection wizard page by page. It lives inside the wizard, so it
 * never opens the wizard itself — each step names the page it needs and
 * `ObjectPickerTour` turns to it. It ends on the Run button rather than
 * pressing it: a detection run costs compute and changes the project, and a
 * tour must never start one. The progress dialog is described, not shown, for
 * the same reason.
 */
const AI_DETECTION_TOUR = {
	id: TOUR_ID.AI_DETECTION,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'How this wizard works', label: 'Tour the detection wizard' },
	steps: [
		{
			target: 'picker-collection',
			title: 'Choose a collection',
			description:
				'A saved set of objects to detect. Manage collections on the Collections page.',
			placement: 'top',
			prepare: 'collection',
		},
		{
			target: 'picker-area',
			title: 'Choose where to look',
			description: 'Draw an area on the map, or search the entire project.',
			placement: 'top',
			prepare: 'area',
		},
		{
			target: 'picker-run',
			title: 'Confirm and run',
			description: 'Starts the job. A progress dialog then follows it until results land.',
			placement: 'top',
			prepare: 'run',
		},
	],
};

/**
 * The video and its control bar, in the order they read: the picture (zoom and the keys, the line), the bigger-view
 * button in its corner, the bar left to right (what it shows, correcting, versions, details and export, the marks'
 * colour), then the in-view strip under it. Offered from a button in the video's top-right corner. The line exists
 * only in line-crossing mode and versions only after a run again, so those steps drop out elsewhere.
 */
const VIDEO_PLAYER_TOUR = {
	id: TOUR_ID.VIDEO_PLAYER,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'Tour the video', label: 'Tour the video player' },
	steps: [
		{
			target: 'video-picture',
			title: 'Zoom and keys',
			description:
				'Scroll to zoom, drag to move. Space plays; arrows step a frame, Shift+arrow 1 s.',
			placement: 'bottom',
		},
		{
			target: 'video-line',
			title: 'Move the line',
			description: 'Drag the line or its ends. The count is recalculated for the new line.',
			placement: 'bottom',
		},
		{
			target: 'video-bigger',
			title: 'A bigger video',
			description:
				'Shrinks the strip and puts the map inside the video. Click again for the normal view.',
			placement: 'left',
		},
		{
			target: 'video-detections',
			title: 'What the video shows',
			description: 'Dots, boxes or off. Turn trails, the line and the map on or off.',
			placement: 'top',
		},
		{
			target: 'video-correct',
			title: 'Correct detections',
			description:
				'Add missed animals, remove wrong ones, or ask the assistant to look again.',
			placement: 'top',
		},
		{
			target: 'video-versions',
			title: 'Earlier versions',
			description: 'Every run again is a new version. Switch between them here.',
			placement: 'top',
		},
		{
			target: 'video-details-export',
			title: 'Details and export',
			description: 'See what was analysed. Download a PDF report, CSV of counts or the map.',
			placement: 'top',
		},
		{
			target: 'video-mark-color',
			title: 'Color of the marks',
			description: 'Hard to see on this ground? Pick another color for the boxes and dots.',
			placement: 'top',
		},
		{
			target: 'video-in-view',
			title: 'Jump to the busy part',
			description: 'How many are in view over time. Click to jump to that moment.',
			placement: 'top',
		},
	],
};

/**
 * The count panel beside the video, top to bottom: the ways of counting, the count, checking it, the changes made,
 * and the two filters. Offered from a button beside the panel's title. The changes list exists only after a fix,
 * so that step drops out until then.
 */
const VIDEO_COUNT_PANEL_TOUR = {
	id: TOUR_ID.VIDEO_COUNT_PANEL,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'How the count works', label: 'Tour the count panel' },
	steps: [
		{
			target: 'video-count-modes',
			title: 'Count it another way',
			description: 'Switch between the total, a line crossing, and the most in view at once.',
			placement: 'left',
		},
		{
			target: 'video-count',
			title: 'Your count',
			description:
				'The total for the way of counting you picked. It updates as you change things.',
			placement: 'left',
		},
		{
			target: 'video-review',
			title: 'Check the count',
			description: 'Doubtful tracks first, then a spot check. Say no to the mistakes.',
			placement: 'left',
		},
		{
			target: 'video-changes',
			title: 'Your changes',
			description:
				'Fixes from correcting and from Check the count land here. Undo any of them.',
			placement: 'left',
		},
		{
			target: 'video-sensitivity',
			title: 'Cut false detections',
			description: 'Raise it to cut false detections. The count updates as you slide.',
			placement: 'left',
		},
		{
			target: 'video-size-filter',
			title: 'Filter by size',
			description: 'Leave out what is too small or too large to be what you counted.',
			placement: 'left',
		},
	],
};

/**
 * The flight map beside the video: where the drone flew, and its controls. Offered from a button in the map's own
 * controls, so it exists only for a video with flight data, shown at full size (the small map inside the video has
 * no controls).
 */
const VIDEO_MAP_TOUR = {
	id: TOUR_ID.VIDEO_MAP,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'Tour the map', label: 'Tour the flight map' },
	steps: [
		{
			target: 'video-flight-map',
			title: 'Where the drone flew',
			description:
				'The flight path, what the camera sees, and each animal on the ground. Click one to jump.',
			placement: 'left',
		},
		{
			target: 'video-map-controls',
			title: 'Follow the drone',
			description:
				'Follow the drone or see the whole flight, and switch satellite and terrain.',
			placement: 'left',
		},
	],
};

/**
 * A new video project once the video is in: what to find, which part to
 * analyse, and the upload. Not offered on the file step before it, which has
 * its own "Before you start" panel. How to count is chosen on the result, so
 * that tour covers it. It ends on the Upload button rather than pressing it.
 */
const VIDEO_NEW_PROJECT_TOUR = {
	id: TOUR_ID.VIDEO_NEW_PROJECT,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'Tour this step', label: 'Tour the video setup' },
	steps: [
		{
			target: 'video-steps',
			title: 'Where you are',
			description: 'Four steps from video to count. Done steps show what you chose.',
			placement: 'right',
		},
		{
			target: 'video-subject',
			title: 'What to count',
			description:
				'The AI suggests a word from the video. Change it and it picks the model that fits.',
			placement: 'left',
		},
		{
			target: 'video-trim',
			title: 'Analyse only what matters',
			description: 'Drag the handles or type the times. A shorter part finishes sooner.',
			placement: 'left',
		},
		{
			target: 'video-blur',
			title: 'Blur people and vehicles',
			description:
				'Pixelates people and vehicles; the original is deleted. Off by default; takes longer.',
			placement: 'left',
		},
		{
			target: 'video-upload',
			title: 'Upload and analyse',
			description:
				'Keep this tab open until the upload ends. The analysis then starts by itself.',
			placement: 'left',
		},
	],
};

/**
 * The correction toolbar on a video, offered from the toolbar itself once the
 * user is correcting — the video tour points at the button that opens it. It
 * describes the tools in the toolbar's order, then the zoom that helps aim them
 * and Run again in the panel, and ends on leaving.
 * The assistant exists once the analysis is finished, and Run again only for a
 * general search with fixes, so those steps drop out elsewhere.
 */
const VIDEO_CORRECTING_TOUR = {
	id: TOUR_ID.VIDEO_CORRECTING,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'How correcting works', label: 'Tour the correction tools' },
	steps: [
		{
			target: 'video-add',
			title: 'Add what it missed',
			description:
				'Click an animal with no dot (A) or box it (B). It is then followed through the video.',
			placement: 'bottom',
		},
		{
			target: 'video-remove',
			title: 'Remove or merge',
			description:
				'Remove (R), then click a dot. With Add on, a dot also merges or removes all like it.',
			placement: 'bottom',
		},
		{
			target: 'video-assistant',
			title: 'Ask the assistant',
			description:
				'Look again at this frame, or say what it missed. You pick what to apply. Costs credits.',
			placement: 'bottom',
		},
		{
			target: 'video-zoom',
			title: 'Zoom in on a small one',
			description:
				'Scroll on the picture to zoom in and drag to move it. The × chip in the bar resets.',
			placement: 'bottom',
		},
		{
			target: 'video-run-again',
			title: 'Run again',
			description: 'Analyse again with your fixes as examples. It becomes a new version.',
			placement: 'left',
		},
		{
			target: 'video-done',
			title: 'Leave correcting',
			description: 'Close the toolbar any time. Each fix was saved as you made it.',
			placement: 'bottom',
		},
	],
};

/**
 * Checking the count, offered from the question under the video once a track is asked about — the results tour
 * points at the card that opens it. It describes the answers without giving one: an answer changes the count. The
 * look-alikes come up under the video after a no, so that step drops out until then.
 */
const VIDEO_CHECK_TOUR = {
	id: TOUR_ID.VIDEO_CHECK,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'How checking works', label: 'Tour checking the count' },
	steps: [
		{
			target: 'video-picture',
			title: 'The track asked about',
			description:
				'Ringed and looped, the rest dimmed. A magnifier in the corner shows it larger.',
			placement: 'bottom',
		},
		{
			target: 'video-check-pictures',
			title: 'Three pictures of it',
			description:
				'Click one to see that moment. One cut by the edge is no reason to say No.',
			placement: 'top',
		},
		{
			target: 'video-check-answer',
			title: 'Answer for the whole track',
			description: 'Yes (Y), No (N) or Not sure (U). No takes it out of the count at once.',
			placement: 'top',
		},
		{
			target: 'video-check-nav',
			title: 'Back, skip, done',
			description:
				'Back (←) works after a skip too. Done (Enter or Esc) any time: your answers are kept.',
			placement: 'top',
		},
		{
			target: 'video-check-up-next',
			title: 'Up next',
			description: 'The tracks still to check. Click one to check it now.',
			placement: 'top',
		},
		{
			target: 'video-check-alike',
			title: 'Look-alikes',
			description:
				'After a No, the tracks that look the same come up here. Tap any that is one, then confirm.',
			placement: 'top',
		},
		{
			target: 'video-check-progress',
			title: 'How far you are',
			description:
				'Doubtful tracks first, then spot checks. If none is wrong, the rest are left as they are.',
			placement: 'left',
		},
		{
			target: 'video-check-effect',
			title: 'What your answers do',
			description:
				'The count now. Undo a removal in Your changes and the track is asked again.',
			placement: 'left',
		},
	],
};

export const TOURS = Object.freeze([
	COLLECTION_VIEW_TOUR,
	AI_DETECTION_TOUR,
	VIDEO_PLAYER_TOUR,
	VIDEO_COUNT_PANEL_TOUR,
	VIDEO_MAP_TOUR,
	VIDEO_NEW_PROJECT_TOUR,
	VIDEO_CORRECTING_TOUR,
	VIDEO_CHECK_TOUR,
]);

/** The tour with this id, or `undefined`. */
export const getTour = tourId => TOURS.find(tour => tour.id === tourId);

/**
 * Whether an announcement is still inside its window. Onboarding tours never
 * expire, so they always pass. An announcement missing `releasedAt` is treated
 * as expired rather than shown forever — a missing date is a mistake, and the
 * quiet failure is the safer one.
 */
export const isWithinWindow = (tour, now = Date.now()) => {
	if (tour?.type !== TOUR_TYPE.ANNOUNCEMENT) return true;
	if (!tour.releasedAt) return false;

	// An unparseable date gives NaN, and NaN compares false — expired.
	return differenceInDays(now, new Date(tour.releasedAt)) < ANNOUNCEMENT_WINDOW_DAYS;
};
